import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";

function Login({ onLogin }) {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for remembered email from prior session
    const rememberedEmail = localStorage.getItem("callbunker_remembered_email");
    const isRemembered = localStorage.getItem("callbunker_remember_me") === "true";
    if (rememberedEmail) {
      setFormData((prev) => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    } else if (isRemembered) {
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post("/api/auth/login", {
        email: formData.email,
        password: formData.password,
        rememberMe,
      });

      // Update remembered credentials in localStorage
      if (rememberMe) {
        localStorage.setItem("callbunker_remembered_email", formData.email);
        localStorage.setItem("callbunker_remember_me", "true");
      } else {
        localStorage.removeItem("callbunker_remembered_email");
        localStorage.removeItem("callbunker_remember_me");
      }

      onLogin(response.data.user, response.data.token, rememberMe);
      navigate("/dashboard");
    } catch (error) {
      setError(error.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "100vh" }}
    >
      <Card style={{ width: "100%", maxWidth: "450px" }}>
        <Card.Body className="p-5">
          <div className="text-center mb-4">
            <img
              src="/icon.png"
              alt="CallBunker Logo"
              className="mb-3"
              style={{ width: "122px", height: "85px" }}
            />
            <h2>Login</h2>
            <p className="text-muted">Protect Your Privacy</p>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Enter your email"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>

              <InputGroup className="position-relative">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Enter your password"
                  className="pe-5" // space for eye toggle
                />

                {/* Eye icon inside the input */}
                <InputGroup.Text
                  as="button"
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="border-0 bg-transparent position-absolute end-0 top-50 translate-middle-y"
                  style={{ zIndex: 10, cursor: "pointer" }}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </InputGroup.Text>
              </InputGroup>
            </Form.Group>

            {/* Remember Me & Forgot Password Row */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Form.Check
                type="checkbox"
                id="remember-me"
                label="Remember me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <Link
                to="/forgot-password"
                className="text-decoration-none"
                style={{ fontSize: "0.9rem" }}
              >
                Forgot password?
              </Link>
            </div>

            <Button
              variant="primary"
              type="submit"
              className="w-100 mb-3"
              disabled={loading}
            >
              {loading ? "Logging in..." : "Login"}
            </Button>

            <div className="text-center">
              <p className="mb-0">
                Don't have an account? <Link to="/signup">Sign up here</Link>
              </p>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Login;
